import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActionIcon, Box, Button, Card, Group, Stack, TagsInput, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { createCpeMapping, deleteCpeMapping } from '../api/cpe-mappings';
import { searchCpeProducts, searchCpeVendors } from '../api/cpe-search';
import type { VendorCpeMapping } from '../api/types';

interface CpeMappingsCardProps {
    // Numeric candidate id — keys the read query / cache invalidation AND the
    // create mutation (VI-473). Unique per vendor, unlike the compound id which
    // collides on `::default` across synthesized rows.
    catalogProductCandidateId: number;
    mappings: VendorCpeMapping[];
}

/**
 * CPE Mappings panel.
 *
 * - Lists existing mappings with a remove button per row.
 * - Inline "Add CPE Mapping" form with two `TagsInput`s (vendors + products)
 *   that suggest known values via `searchCpeVendors` / `searchCpeProducts`.
 *   We use TagsInput rather than two single comboboxes so the user can fan
 *   out a single submit into multiple mappings (one vendor + N products
 *   creates N rows; N vendors + 0 products creates N rows).
 */
export const CpeMappingsCard = ({
    catalogProductCandidateId,
    mappings,
}: CpeMappingsCardProps): React.JSX.Element => {
    const queryClient = useQueryClient();
    const [isAdding, setIsAdding] = useState(false);
    const [vendorTags, setVendorTags] = useState<string[]>([]);
    const [productTags, setProductTags] = useState<string[]>([]);
    const [vendorSuggestions, setVendorSuggestions] = useState<string[]>([]);
    const [productSuggestions, setProductSuggestions] = useState<string[]>([]);

    const invalidate = useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: ['cpe-mappings', catalogProductCandidateId],
            }),
        [queryClient, catalogProductCandidateId],
    );

    const addMutation = useMutation({
        mutationFn: (items: { cpe_vendor: string; cpe_product?: string }[]) =>
            Promise.all(
                items.map((data) =>
                    createCpeMapping({
                        catalog_product_candidate_id: catalogProductCandidateId,
                        cpe_vendor: data.cpe_vendor,
                        cpe_product: data.cpe_product || undefined,
                        match_type: 'manual',
                        confidence: 'high',
                        source: 'vendor-portal',
                    }),
                ),
            ),
        onSuccess: async () => {
            await invalidate();
            setIsAdding(false);
            setVendorTags([]);
            setProductTags([]);
            setVendorSuggestions([]);
            setProductSuggestions([]);
            notifications.show({
                color: 'green',
                message: 'CPE mapping(s) added.',
            });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to add CPE mapping',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const removeMutation = useMutation({
        mutationFn: (id: number) => deleteCpeMapping(id),
        onSuccess: async () => {
            await invalidate();
            notifications.show({ color: 'green', message: 'Removed.' });
        },
        onError: (err) => {
            notifications.show({
                color: 'red',
                title: 'Failed to remove CPE mapping',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const handleAdd = useCallback(() => {
        if (vendorTags.length === 0) {
            return;
        }
        const items: { cpe_vendor: string; cpe_product?: string }[] = [];
        for (const vendor of vendorTags) {
            if (productTags.length === 0) {
                items.push({ cpe_vendor: vendor });
            } else {
                for (const product of productTags) {
                    items.push({ cpe_vendor: vendor, cpe_product: product });
                }
            }
        }
        addMutation.mutate(items);
    }, [addMutation, vendorTags, productTags]);

    // VI-517: no minimum query length. An empty q returns the top matches by
    // CVE count, so focusing the field surfaces suggestions immediately; a
    // non-empty q narrows by prefix.
    const handleVendorSearch = useCallback(async (q: string) => {
        const results = await searchCpeVendors({ q, limit: 10 });
        setVendorSuggestions(results.map((r) => r.value));
    }, []);

    const handleProductSearch = useCallback(
        async (q: string) => {
            if (vendorTags.length === 0) {
                setProductSuggestions([]);
                return;
            }
            const all = await Promise.all(
                vendorTags.map((v) => searchCpeProducts({ q, vendor: v, limit: 10 })),
            );
            const merged = new Set<string>();
            for (const results of all) {
                for (const r of results) {
                    merged.add(r.value);
                }
            }
            setProductSuggestions(Array.from(merged));
        },
        [vendorTags],
    );

    return (
        <Card withBorder radius="md" padding="lg" data-testid="CpeMappingsCard">
            <Group justify="space-between" mb="md">
                <Title order={4}>CPE Mappings</Title>
                {!isAdding && (
                    <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => setIsAdding(true)}
                        data-testid="AddCpeMappingButton"
                    >
                        Add CPE Mapping
                    </Button>
                )}
            </Group>

            <Stack gap="xs">
                {mappings.length === 0 && !isAdding ? (
                    <Text c="dimmed" size="sm" data-testid="CpeMappingsEmpty">
                        No CPE mappings configured for this vendor.
                    </Text>
                ) : (
                    mappings.map((m) => (
                        <Group key={m.id} justify="space-between" data-testid="CpeMappingRow">
                            <Group gap="xs">
                                <Text size="sm" fw={500}>
                                    {m.cpe_vendor}
                                </Text>
                                <Text size="sm" c="dimmed">
                                    {m.cpe_product ?? '(all products)'}
                                </Text>
                            </Group>
                            <ActionIcon
                                color="red"
                                variant="subtle"
                                aria-label="Remove CPE mapping"
                                onClick={() => removeMutation.mutate(m.id)}
                                disabled={removeMutation.isPending}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    ))
                )}
            </Stack>

            {isAdding && (
                <Box
                    mt="md"
                    p="md"
                    style={{
                        border: '1px solid var(--mantine-color-default-border)',
                        borderRadius: 'var(--mantine-radius-md)',
                    }}
                    data-testid="AddCpeMappingForm"
                >
                    <Stack gap="md">
                        <TagsInput
                            label="CPE Vendors"
                            description="Focus to see the most common vendors, or type to search. Press Enter to accept a value."
                            value={vendorTags}
                            onChange={(values) => {
                                setVendorTags(values);
                                if (values.length === 0) {
                                    setProductTags([]);
                                    setProductSuggestions([]);
                                }
                            }}
                            data={vendorSuggestions}
                            onFocus={() => void handleVendorSearch('')}
                            onSearchChange={(q) => void handleVendorSearch(q)}
                            placeholder="e.g. microsoft"
                        />
                        <TagsInput
                            label="CPE Products (optional)"
                            description={
                                vendorTags.length === 0
                                    ? 'Select a vendor first.'
                                    : 'Leave empty to map all products of the selected vendor(s).'
                            }
                            disabled={vendorTags.length === 0}
                            value={productTags}
                            onChange={setProductTags}
                            data={productSuggestions}
                            onFocus={() => void handleProductSearch('')}
                            onSearchChange={(q) => void handleProductSearch(q)}
                            placeholder="e.g. office"
                        />
                        <Group justify="flex-end" gap="sm">
                            <Button
                                variant="subtle"
                                onClick={() => {
                                    setIsAdding(false);
                                    setVendorTags([]);
                                    setProductTags([]);
                                    setVendorSuggestions([]);
                                    setProductSuggestions([]);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAdd}
                                loading={addMutation.isPending}
                                disabled={vendorTags.length === 0}
                            >
                                Save
                            </Button>
                        </Group>
                    </Stack>
                </Box>
            )}
        </Card>
    );
};
